/* Écrire ce que l'aperçu a validé.
 *
 * Un import doit pouvoir se rejouer : on relance le même fichier corrigé sans
 * créer de doublon. Chaque type a donc sa clé naturelle — le code pour une
 * usine, usine + référence pour un produit, l'email pour un agent — et l'écriture
 * passe par un `upsert` sur cette clé. Les contacts n'en ont pas en base : on
 * les rapproche par leur nom chez le même parent, avant d'écrire.
 */

import { supabase } from "@/lib/supabase";
import { normalise } from "@/domain/import";
import type { LigneImport, Valeur } from "@/domain/import";
import type { CleImport } from "@/domain/modeles-import";
import { poserPrixAgentLot, poserPrixUsineLot } from "@/features/prix/actions";
import { DOMAINES } from "@/domain/modeles-import";

export interface Resultat {
  creees: number;
  majs: number;
  /* Un prix reposé à l'identique n'est pas « mis à jour » : il est inchangé.
     Le mot compte, parce qu'il dit que l'archive n'a pas grossi pour rien. */
  inchangees?: number;
}

const PAQUET = 400;

function txt(v: Valeur | undefined): string | null {
  const s = typeof v === "string" ? v.trim() : v == null ? "" : String(v);
  return s.length ? s : null;
}

function num(v: Valeur | undefined): number | null {
  return typeof v === "number" ? v : null;
}

function bool(v: Valeur | undefined, defaut: boolean): boolean {
  return typeof v === "boolean" ? v : defaut;
}

/** « customer, finance » → les valeurs admises, les autres sont ignorées. */
function domaines(v: Valeur | undefined): string[] {
  const brut = txt(v);
  if (!brut) return [];
  const voulus = brut.split(/[,;/|]/).map(normalise).filter(Boolean);
  return DOMAINES.filter((d) => voulus.includes(normalise(d)));
}

async function parPaquets<T>(
  elements: readonly T[],
  ecrire: (lot: T[]) => PromiseLike<{ error: { message: string } | null }>,
): Promise<void> {
  for (let i = 0; i < elements.length; i += PAQUET) {
    const { error } = await ecrire(elements.slice(i, i + PAQUET));
    if (error) throw new Error(error.message);
  }
}

export async function ecrire(cle: CleImport, lignes: readonly LigneImport[]): Promise<Resultat> {
  const v = lignes.map((l) => l.valeurs);
  switch (cle) {
    case "usines":
      return upsert("usines", "code", v.map((r) => ({
        code: txt(r["code"]),
        nom: txt(r["nom"]),
        categorie: txt(r["categorie"]),
        ville: txt(r["ville"]),
        pays: txt(r["pays"]),
        prefixe_facture: txt(r["prefixe_facture"]),
        actif: bool(r["actif"], true),
      })));

    case "produits":
      return upsert("produits", "usine_id,ref", v.map((r) => ({
        usine_id: txt(r["usine_id"]),
        ref: txt(r["ref"]),
        nom: txt(r["nom"]),
        gamme: txt(r["gamme"]),
        conditionnement: txt(r["conditionnement"]),
        prix: num(r["prix"]),
        actif: bool(r["actif"], true),
      })));

    /* Les prix ne s'écrasent plus : chaque pose ferme la période précédente
       et en ouvre une nouvelle. C'est la base qui le fait, pas nous — et elle
       ne compte que ce qui a réellement changé, un prix reposé à l'identique
       n'encombrant pas l'archive. */
    case "prix_usine": {
      const lignes = v
        .map((r) => ({ produit_id: txt(r["produit_id"]) ?? "", prix: num(r["prix"]) ?? 0 }))
        .filter((l) => l.produit_id);
      const poses = await poserPrixUsineLot(lignes);
      return { creees: poses, majs: 0, inchangees: lignes.length - poses };
    }

    case "prix_agent": {
      const lignes = v
        .map((r) => ({
          agent_id: txt(r["agent_id"]) ?? "",
          produit_id: txt(r["produit_id"]) ?? "",
          prix_mini: num(r["prix_mini"]) ?? 0,
        }))
        .filter((l) => l.agent_id && l.produit_id);
      const poses = await poserPrixAgentLot(lignes);
      return { creees: poses, majs: 0, inchangees: lignes.length - poses };
    }

    case "agents":
      return upsert("profils", "email", v.map((r) => ({
        nom: txt(r["nom"]),
        email: (txt(r["email"]) ?? "").toLowerCase(),
        niveau: txt(r["niveau"]) ?? "agent",
        actif: bool(r["actif"], true),
      })));

    case "contacts":
      return contacts(v);
  }
}

async function upsert(
  table: string,
  conflit: string,
  rangees: readonly Record<string, unknown>[],
): Promise<Resultat> {
  if (!rangees.length) return { creees: 0, majs: 0 };
  const avant = await compter(table);
  await parPaquets(rangees, (lot) =>
    supabase.from(table).upsert(lot, { onConflict: conflit, ignoreDuplicates: false }));
  const apres = await compter(table);
  const creees = Math.max(0, apres - avant);
  return { creees, majs: rangees.length - creees };
}

async function compter(table: string): Promise<number> {
  const { count } = await supabase.from(table).select("*", { count: "exact", head: true });
  return count ?? 0;
}

/**
 * Les contacts n'ont pas de clé en base : deux personnes peuvent porter le même
 * nom chez deux usines différentes, et c'est légitime. On rapproche donc par
 * parent + nom, et on met à jour ce qui existe plutôt que de l'empiler.
 */
async function contacts(rangees: readonly Record<string, Valeur>[]): Promise<Resultat> {
  const cote = (r: Record<string, Valeur>) =>
    txt(r["usine_id"]) ? ("usine" as const) : ("client" as const);

  let creees = 0;
  let majs = 0;

  for (const groupe of ["usine", "client"] as const) {
    const lot = rangees.filter((r) => cote(r) === groupe);
    if (!lot.length) continue;

    const table = groupe === "usine" ? "contacts_usine" : "contacts_client";
    const champParent = groupe === "usine" ? "usine_id" : "client_id";
    const parents = [...new Set(lot.map((r) => txt(r[champParent])).filter(Boolean))] as string[];

    const { data, error } = await supabase
      .from(table)
      .select(`id, nom, ${champParent}`)
      .in(champParent, parents);
    if (error) throw new Error(error.message);

    const connus = new Map<string, string>();
    for (const c of (data ?? []) as Record<string, string>[]) {
      connus.set(`${c[champParent]}\u0000${normalise(c["nom"])}`, c["id"] as string);
    }

    const neufs: Record<string, unknown>[] = [];
    const repris: Record<string, unknown>[] = [];

    for (const r of lot) {
      const base: Record<string, unknown> = {
        [champParent]: txt(r[champParent]),
        nom: txt(r["nom"]),
        email: txt(r["email"]),
        tel: txt(r["tel"]),
        domaines: domaines(r["domaines"]),
      };
      if (groupe === "usine") base["generique"] = bool(r["generique"], false);
      const id = connus.get(`${txt(r[champParent])}\u0000${normalise(r["nom"])}`);
      if (id) repris.push({ ...base, id });
      else neufs.push(base);
    }

    if (neufs.length) {
      await parPaquets(neufs, (l) => supabase.from(table).insert(l));
      creees += neufs.length;
    }
    if (repris.length) {
      await parPaquets(repris, (l) => supabase.from(table).upsert(l, { onConflict: "id" }));
      majs += repris.length;
    }
  }

  return { creees, majs };
}

/** Lignes qu'un fichier de contacts ne peut pas poser : ni usine, ni client — ou les deux. */
export function contactsMalRattaches(lignes: readonly LigneImport[]): number[] {
  return lignes
    .filter((l) => {
      const u = Boolean(txt(l.valeurs["usine_id"]));
      const c = Boolean(txt(l.valeurs["client_id"]));
      return u === c;
    })
    .map((l) => l.ligne);
}
